import { Formik } from 'formik';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  ActionButton,
  Buttons,
  CancelButton,
  Confirmation,
  DeleteButton,
  Drawer,
  Field,
  Form,
  FormMessage,
  Icon,
  ProjectTaskTagMultiSelect,
  Tab,
  Tabs,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import { colors } from '~/styles';
import { emptyStringToNull, mergeValues } from '~/utils';
import ProjectTaskHistory from './ProjectTaskHistory';
import ProjectTaskRelationships from './ProjectTaskRelationships';
import ShiftBlockedTasksConfirmation from './ShiftBlockedTasksConfirmation';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 1.625rem;
  margin-bottom: 1.625rem;
`;

function ProjectTaskMilestoneForm({ task, project, onSaved, onClose, onDelete }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ isSubmitting, status, message, saved }, form] = useForm();
  const [hasDependencies, setHasDependencies] = useState(true);
  const confirmation = useConfirmation();
  const [tabIndex, setTabIndex] = useState(0);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).hasDependencies();
      setHasDependencies(data);
    })();
  }, [api, workspace.id, project.id, task]);

  const title = 'Edit Task';
  useDocumentTitle(title);

  const formRef = useRef();
  const firstFieldRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  const defaultValues = {
    code: '',
    description: '',
    name: '',
    taskCategory: null,
    statusId: 'not_started',
    start: null,
    tags: [],
    parent: null,
    predecessors: [],
    successors: [],
  };

  const initialValues = mergeValues(defaultValues, task);

  const tabErrors = {
    task: ['code', 'description', 'name', 'start', 'statusId'],
  };

  return (
    <Drawer
      title={title}
      isOpen
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            let shiftSuccessors = true;
            const startChanged = task.start && values.start && task.start !== values.start;
            if (project.useDependencies && startChanged) {
              const { data: blockedTasks } = await api.www
                .workspaces(workspace.id)
                .projects(project.id)
                .tasks(task.id)
                .scheduledSuccessors({ successorIds: values.successors.map((s) => s.id) });
              if (blockedTasks.length > 0) {
                const result = await confirmation.prompt((resolve) => (
                  <ShiftBlockedTasksConfirmation taskName={task.name} blockedTasks={blockedTasks} resolve={resolve} />
                ));
                if (!result) return;
                shiftSuccessors = result.shiftSuccessors;
              }
            }

            const completingTask = values.statusId === 'completed' && task.statusId !== 'completed';
            // Use the staged predecessors — the Relationships editor can change them in this same save.
            const hasOpenPredecessors = values.predecessors?.some((p) => p.statusId !== 'completed');
            if (project.useDependencies && completingTask && hasOpenPredecessors) {
              const confirmed = await confirmation.prompt((resolve) => (
                <Confirmation resolve={resolve}>
                  This task is blocked by tasks that haven't been completed yet. Are you sure you want to mark it as
                  completed?
                </Confirmation>
              ));
              if (!confirmed) return;
            }

            form.submit(formRef.current.status.action);

            const body = emptyStringToNull({
              ..._.omit(values, ['tags', 'parent', 'taskCategory', 'predecessors', 'successors']),
              end: values.start ?? null,
              projectTaskTagAssignments: values.tags.map((tag) => ({ projectTaskTagId: tag.id })),
              predecessors: values.predecessors.map((p) => ({ predecessorId: p.id })),
              successors: values.successors.map((s) => ({ successorId: s.id })),
              parentId: values.parent?.id ?? null,
              projectTaskCategoryId: values.taskCategory?.id ?? null,
              shiftSuccessors,
            });

            const { data } = await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).upsert(body);

            await onSaved(data);

            toast.success('Task saved successfully');
            form.save(formRef.current.status.action);
            closeDrawer();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        async function handleDelete() {
          await onDelete(task);
          closeDrawer();
        }

        return (
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            initialStatus={{ next: 'done' }}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              code: Yup.string().label('Task Code').max(255),
              description: Yup.string().label('Notes').max(5000),
              name: Yup.string().label('Task Name').max(255).required(),
              start: Yup.date().label('Date').nullable(),
              statusId: Yup.string().label('Status').required(),
            })}>
            {({ errors, values, setFieldValue, submitForm, setStatus, setValues }) => {
              const submit = async (action) => {
                setStatus({ action });
                submitForm();
              };

              return (
                <Form>
                  <Tabs selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
                    <Tab>
                      Task
                      {tabErrors.task.some((key) => !!errors[key]) && (
                        <Icon icon="exclamation-circle" color={colors.danger} spaceLeft />
                      )}
                    </Tab>
                    {task.permissions.manage && <Tab>History</Tab>}
                  </Tabs>
                  <Content>
                    {status && <FormMessage.Error>{message}</FormMessage.Error>}

                    {
                      [
                        <React.Fragment key="task">
                          <Form.Section title="Basic Information">
                            <Form.Control>
                              <Field.Text ref={firstFieldRef} name="name" placeholder="Task Name" maxLength={255} />
                            </Form.Control>
                            <Form.Control>
                              <Field.ProjectTaskCategorySelect
                                name="taskCategory"
                                placeholder="Milestone Task Category"
                                type="milestone"
                                clearable={false}
                              />
                            </Form.Control>
                            <Form.Control>
                              <Field.ProjectTaskStatusSelect
                                name="statusId"
                                placeholder="Status"
                                exclude={['in_progress']}
                              />
                            </Form.Control>
                            <Form.Control>
                              <Field.ProjectTaskSelect
                                name="parent"
                                placeholder="Parent Task"
                                project={project}
                                disableMilestones
                              />
                            </Form.Control>
                            <Form.Control>
                              <Field.DayPicker name="start" placeholder="Date" />
                            </Form.Control>
                            <Form.Control>
                              <Field.Text name="code" placeholder="Task Code" maxLength={255} />
                            </Form.Control>
                            <Form.Control>
                              <Field.Control>
                                <ProjectTaskTagMultiSelect
                                  name="tags"
                                  placeholder="Tags"
                                  value={values.tags}
                                  allowNew
                                  onChange={({ target: { value } }) => setFieldValue('tags', value)}
                                />
                              </Field.Control>
                            </Form.Control>
                            <Form.Control>
                              <Field.TextArea name="description" placeholder="Notes" rows={4} maxLength={5000} />
                            </Form.Control>
                          </Form.Section>

                          {project.useDependencies &&
                            (values.predecessors?.length > 0 ||
                              values.successors?.length > 0 ||
                              task.permissions.manage) && (
                              <Form.Section title="Relationships">
                                <ProjectTaskRelationships
                                  project={project}
                                  taskId={task.id}
                                  predecessors={values.predecessors}
                                  successors={values.successors}
                                  disabled={!task.permissions.manage}
                                  onChange={({ predecessors, successors }) =>
                                    setValues({ ...values, predecessors, successors })
                                  }
                                />
                              </Form.Section>
                            )}
                        </React.Fragment>,
                        <ProjectTaskHistory key="history" projectId={project.id} taskId={task.id} />,
                      ][tabIndex]
                    }
                  </Content>

                  <Drawer.Actions>
                    {task.permissions.manage && _.isFunction(onDelete) && (
                      <DeleteButton disabled={hasDependencies} onClick={handleDelete}>
                        Delete
                      </DeleteButton>
                    )}

                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                      <ActionButton
                        isLoading={isSubmitting === 'close'}
                        ok={saved === 'close'}
                        onClick={() => submit('close')}>
                        Save &amp; Close
                      </ActionButton>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

export default ProjectTaskMilestoneForm;
